import { useRef, useState } from "react";
import { FormContext } from "./FormContext";
import { createStore } from "zustand";
import { Formik, FormikConfig, FormikHelpers, FormikValues } from "formik";

export interface FormationContextType {
  toggleEdit: (isEditing?: boolean) => void;
  isEditing: boolean;
}

export type FormationSubmitHandler<T> = (
  values: T,
  formikHelpers: FormikHelpers<T>,
  formation: FormationContextType,
) => void | Promise<unknown>;

export type FormationProps<T> = {
  initialEditingState?: boolean;
  onSubmit?: FormationSubmitHandler<T>;
} & Omit<FormikConfig<T>, "onSubmit">;

export function Formation<T extends FormikValues>({
  initialEditingState = true,
  onSubmit,
  ...props
}: FormationProps<T>) {
  const [store] = useState(() =>
    createStore<FormationContextType>((set) => ({
      isEditing: initialEditingState,
      toggleEdit: (isEditing?: boolean) =>
        set((state) => ({
          isEditing: isEditing ?? !state.isEditing,
        })),
    })),
  );

  const previousInitialEditingState = useRef(initialEditingState);

  if (previousInitialEditingState.current !== initialEditingState) {
    previousInitialEditingState.current = initialEditingState;
    store.setState({ isEditing: initialEditingState });
  }

  return (
    <FormContext value={store}>
      <Formik<T>
        {...props}
        onSubmit={async (values, formikHelpers) => {
          try {
            await onSubmit?.(values, formikHelpers, store.getState());
          } catch (e) {
            console.error({
              message: "Error submitting form",
              error: e,
            });
          }
        }}
      />
    </FormContext>
  );
}
